import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { Plus, Trash2, Copy, Check, X } from 'lucide-react'
import api from '../services/api'
import Card from '../components/Card'
import LoadingSpinner from '../components/LoadingSpinner'
import { getToken, clearAuth } from '../utils/userData'

const FacilitatorTestimonialManage = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [testimonials, setTestimonials] = useState([])
  const [invites, setInvites] = useState([])
  const [showInviteForm, setShowInviteForm] = useState(false)
  const [recipientLabel, setRecipientLabel] = useState('')
  const [creating, setCreating] = useState(false)
  const [copiedToken, setCopiedToken] = useState(null)

  useEffect(() => {
    const token = getToken()
    if (!token) {
      navigate('/')
      return
    }
    fetchData()
  }, [navigate])

  const fetchData = async () => {
    try {
      setLoading(true)
      const response = await api.get('/api/facilitators/testimonials')
      if (response.data.success) {
        setTestimonials(response.data.data.testimonials || [])
        setInvites(response.data.data.invites || [])
      }
    } catch (err) {
      if (err.response?.status === 401) {
        clearAuth()
        navigate('/')
      }
      console.error('Error fetching testimonials:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateInvite = async (e) => {
    e.preventDefault()
    if (!recipientLabel.trim()) return
    try {
      setCreating(true)
      await api.post('/api/facilitators/testimonials/invites', { recipientLabel })
      setRecipientLabel('')
      setShowInviteForm(false)
      fetchData()
    } catch (err) {
      console.error('Error creating invite:', err)
    } finally {
      setCreating(false)
    }
  }

  const handleRevokeInvite = async (id) => {
    if (!window.confirm('Revoke this invite link? This cannot be undone.')) return
    try {
      await api.delete(`/api/facilitators/testimonials/invites/${id}`)
      fetchData()
    } catch (err) {
      console.error('Error revoking invite:', err)
    }
  }

  const handleToggleTestimonial = async (testimonial) => {
    try {
      await api.put(`/api/facilitators/testimonials/${testimonial.id}`, { active: !testimonial.active })
      fetchData()
    } catch (err) {
      console.error('Error toggling testimonial:', err)
    }
  }

  const handleDeleteTestimonial = async (id) => {
    if (!window.confirm('Delete this testimonial? This cannot be undone.')) return
    try {
      await api.delete(`/api/facilitators/testimonials/${id}`)
      fetchData()
    } catch (err) {
      console.error('Error deleting testimonial:', err)
    }
  }

  const copyLink = (token) => {
    const link = `${window.location.origin}/testimonial/${token}`
    navigator.clipboard.writeText(link)
    setCopiedToken(token)
    setTimeout(() => setCopiedToken(null), 2000)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-cream to-primary-100 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-cream to-primary-100">
      <div className="container mx-auto px-4 py-12">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Testimonials</h1>
          <p className="text-gray-600">Invite clients to share feedback, then choose what appears on your profile.</p>
        </motion.div>

        {/* Invite Links */}
        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Invite Links</h2>
            <button
              onClick={() => setShowInviteForm(v => !v)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors text-sm"
            >
              <Plus className="w-4 h-4" />
              Create Invite
            </button>
          </div>

          {showInviteForm && (
            <Card className="mb-4 p-5">
              <form onSubmit={handleCreateInvite} className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recipient Label <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    autoFocus
                    value={recipientLabel}
                    onChange={(e) => setRecipientLabel(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g., Priya – April workshop"
                  />
                </div>
                <button
                  type="submit"
                  disabled={creating}
                  className="px-5 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 whitespace-nowrap"
                >
                  {creating ? 'Creating...' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowInviteForm(false)}
                  className="p-3 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </form>
            </Card>
          )}

          {invites.length === 0 ? (
            <Card className="text-center py-10">
              <p className="text-gray-500">No invite links yet. Create one to invite a client.</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {invites.map((invite) => (
                <Card key={invite.id} className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 text-sm">{invite.recipientLabel}</p>
                      <p className="text-xs text-gray-400 mt-0.5 truncate">
                        {window.location.origin}/testimonial/{invite.token}
                      </p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          invite.status === 'expired' ? 'bg-red-100 text-red-700' :
                          invite.status === 'used' ? 'bg-green-100 text-green-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {invite.status || 'pending'}
                        </span>
                        <span className="text-xs text-gray-400">
                          {new Date(invite.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => copyLink(invite.token)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                      >
                        {copiedToken === invite.token
                          ? <Check className="w-3.5 h-3.5 text-green-600" />
                          : <Copy className="w-3.5 h-3.5" />}
                        {copiedToken === invite.token ? 'Copied!' : 'Copy Link'}
                      </button>
                      <button
                        onClick={() => handleRevokeInvite(invite.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Revoke invite"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </motion.section>

        {/* Received Testimonials */}
        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Received Testimonials</h2>

          {testimonials.length === 0 ? (
            <Card className="text-center py-10">
              <p className="text-gray-500">No testimonials yet. Share an invite link with your clients.</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {testimonials.map((testimonial) => (
                <Card key={testimonial.id} className="p-5">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-700 italic mb-3 leading-relaxed">&ldquo;{testimonial.quote}&rdquo;</p>
                      <p className="font-semibold text-gray-900 text-sm">{testimonial.authorName}</p>
                      {testimonial.authorRole && (
                        <p className="text-gray-500 text-xs mt-0.5">{testimonial.authorRole}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${testimonial.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {testimonial.active ? 'Visible' : 'Hidden'}
                      </span>
                      <button
                        onClick={() => handleToggleTestimonial(testimonial)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                          testimonial.active
                            ? 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                            : 'text-green-700 bg-green-100 hover:bg-green-200'
                        }`}
                      >
                        {testimonial.active ? 'Hide' : 'Show'}
                      </button>
                      <button
                        onClick={() => handleDeleteTestimonial(testimonial.id)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </motion.section>
      </div>
    </div>
  )
}

export default FacilitatorTestimonialManage
